import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * Spec 230 — native <dialog> + showModal().
 *
 * Focus trap, Esc, the top layer and an inert background all come from the
 * platform. The three hand-rolled modals in this app each re-implemented a
 * subset of that and none of them got focus return right.
 *
 * Padding sits on an inner wrapper so a click on the backdrop is unambiguous:
 * with padding on the <dialog> itself, a click in the padding is a click on the
 * dialog and dismissal silently stops working near the edges.
 */
export function Dialog({
  open, onClose, title, dismissible = true, children,
}: {
  open: boolean; onClose: () => void; title: string;
  dismissible?: boolean; children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onCancel = (e: Event) => {
      if (!dismissible) { e.preventDefault(); return; }
      onClose();
    };
    el.addEventListener('cancel', onCancel);
    return () => el.removeEventListener('cancel', onCancel);
  }, [dismissible, onClose]);

  return (
    <dialog
      ref={ref}
      className="hub-plate"
      onClick={(e) => { if (dismissible && e.target === ref.current) onClose(); }}
    >
      <div className="p-0">
        <h2>{title}</h2>
        {children}
      </div>
    </dialog>
  );
}
